// Client-side carrier cost ranking for the schedule-table carrier dropdown.
//
// Consumes the pricing context pushed by the backend (see apiService.tsx's
// `carrier_rate_context`, sourced from functions/pricing/pricing_context.py) —
// an all-in $/bbl figure (base trucking rate + fuel surcharge already merged
// server-side) per carrier per exact mileage. This module does no diesel-price
// or fsc_type math of its own; it only looks up a row's mileage and ranks.

export interface CarrierRateContext {
    generatedAt: string;
    assumedVolumeBbl: number;
    // Metadata only — not currently consulted by rankCarrierCosts, which reads
    // straight from `rates`; kept typed so it isn't silently invisible to callers.
    carriers: Array<{ id: number; name: string; paddNum: number }>;
    // carrier_id (as a string key, matching the backend's JSON object keys) ->
    // one entry per exact mileage trucking_rates has a row for.
    rates: Record<string, Array<{ mileage: number; allInRatePerBbl: number }>>;
}

export interface CarrierCostOption {
    value: any;
    label: string;
    allInCost: number | null;
    deltaVsBaseline: number | null;
    isCheapest: boolean;
    displayLabel: string;
}

function findRateAtMileage(
    rateContext: CarrierRateContext,
    carrierId: any,
    mileage: number
): number | null {
    const rows = rateContext.rates[String(carrierId)];
    if (!rows || rows.length === 0) return null;
    const exact = rows.find(r => r.mileage === mileage);
    return exact ? exact.allInRatePerBbl : null;
}

function byCostAscending(a: CarrierCostOption, b: CarrierCostOption): number {
    if (a.allInCost === null && b.allInCost === null) return 0;
    if (a.allInCost === null) return 1;
    if (b.allInCost === null) return -1;
    return a.allInCost - b.allInCost;
}

/**
 * Ranks a row's carrier dropdown options by approximate all-in cost.
 *
 * The returned list is SORTED cheapest-first (priced carriers ascending by
 * allInCost; carriers with no rate entry — no trucking_rates coverage at this
 * mileage — are appended after, in their original order, since there's no
 * price to rank them by).
 *
 * Labeling rule (covers both "nothing picked yet" and "reviewing an existing
 * pick" with one formula, no branching into separate code paths):
 *   baseline = currentCarrierId ? cost[currentCarrierId] : cheapestCost
 *   delta = cost[option] - baseline
 *   - the overall-cheapest option gets " — Best" when it equals the baseline
 *   - delta < 0 renders " (-$N cheaper)"
 *   - delta > 0 renders " (+$N)"
 *   - delta === 0 on a non-cheapest current selection gets no suffix
 *
 * If totalMileage is missing (pickup/delivery legs not set yet) or a carrier
 * has no rate entry, that carrier's label is left unmodified — never show a
 * fabricated $0.
 */
export function rankCarrierCosts(
    carrierOptions: Array<{ value: any; label: string; [key: string]: any }>,
    totalMileage: string | number | null | undefined,
    rateContext: CarrierRateContext | null,
    currentCarrierId: any | null
): CarrierCostOption[] {
    if (!rateContext || totalMileage === null || totalMileage === undefined || totalMileage === '') {
        return carrierOptions.map(opt => ({
            ...opt,
            allInCost: null,
            deltaVsBaseline: null,
            isCheapest: false,
            displayLabel: opt.label,
        }));
    }

    const mileage = typeof totalMileage === 'string' ? parseFloat(totalMileage) : totalMileage;
    if (!Number.isFinite(mileage)) {
        return carrierOptions.map(opt => ({
            ...opt,
            allInCost: null,
            deltaVsBaseline: null,
            isCheapest: false,
            displayLabel: opt.label,
        }));
    }

    const volume = rateContext.assumedVolumeBbl || 180;
    const costs: Array<CarrierCostOption> = carrierOptions.map(opt => {
        const rate = findRateAtMileage(rateContext, opt.value, mileage);
        const allInCost = rate !== null ? rate * volume : null;
        return { ...opt, allInCost, deltaVsBaseline: null, isCheapest: false, displayLabel: opt.label };
    });

    const priced = costs.filter(c => c.allInCost !== null) as Array<CarrierCostOption & { allInCost: number }>;
    if (priced.length === 0) return costs;

    const cheapestCost = Math.min(...priced.map(c => c.allInCost));
    const currentEntry = currentCarrierId != null
        ? priced.find(c => c.value === currentCarrierId)
        : undefined;
    const baseline = currentEntry ? currentEntry.allInCost : cheapestCost;

    return costs.map(c => {
        if (c.allInCost === null) return c;
        const delta = c.allInCost - baseline;
        const isCheapest = c.allInCost === cheapestCost;
        let displayLabel = c.label;
        if (isCheapest && delta === 0) {
            displayLabel = `${c.label} — Best`;
        } else if (delta < 0) {
            displayLabel = `${c.label} (-$${Math.abs(delta).toFixed(0)} cheaper)`;
        } else if (delta > 0) {
            displayLabel = `${c.label} (+$${delta.toFixed(0)})`;
        }
        return { ...c, deltaVsBaseline: delta, isCheapest, displayLabel };
    }).sort(byCostAscending);
}
